import { useState, useRef, useEffect, useCallback } from 'react';

interface UseDraggableOptions {
  /**
   * 初始位置
   */
  initialPosition?: { x: number; y: number };
  /**
   * 边界容器选择器，如果不提供则使用 window
   */
  boundarySelector?: string;
  /**
   * 是否启用拖拽
   */
  enabled?: boolean;
}

interface Position {
  x: number;
  y: number;
}

/**
 * 可拖拽元素的 Hook
 * 提供拖拽功能和边界检测
 */
export const useDraggable = (options: UseDraggableOptions = {}) => {
  const { initialPosition, boundarySelector, enabled = true } = options;

  const [position, setPosition] = useState<Position>(initialPosition || { x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const elementRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<Position>({ x: 0, y: 0 });
  const mouseStartRef = useRef<Position>({ x: 0, y: 0 });
  const touchStartRef = useRef<Position>({ x: 0, y: 0 });

  /**
   * 获取边界信息
   * 对于 fixed 定位的元素，边界是相对于视口的
   */
  const getBoundary = useCallback(() => {
    if (boundarySelector) {
      const boundaryElement = document.querySelector(boundarySelector) as HTMLElement;
      if (boundaryElement) {
        const rect = boundaryElement.getBoundingClientRect();
        // 返回相对于视口的边界
        return {
          left: rect.left,
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom
        };
      }
    }

    // 默认使用 window 作为边界
    return {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight
    };
  }, [boundarySelector]);

  /**
   * 限制位置在边界内
   */
  const constrainPosition = useCallback(
    (x: number, y: number): Position => {
      if (!elementRef.current) return { x, y };

      const boundary = getBoundary();
      const elementRect = elementRef.current.getBoundingClientRect();
      const elementWidth = elementRect.width;
      const elementHeight = elementRect.height;

      // 计算约束后的位置
      const constrainedX = Math.max(boundary.left, Math.min(x, boundary.right - elementWidth));
      const constrainedY = Math.max(boundary.top, Math.min(y, boundary.bottom - elementHeight));

      return { x: constrainedX, y: constrainedY };
    },
    [getBoundary]
  );

  /**
   * 开始拖拽的通用处理函数
   */
  const startDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (!enabled || !elementRef.current) return false;

      setIsDragging(true);

      const elementRect = elementRef.current.getBoundingClientRect();
      dragStartRef.current = {
        x: elementRect.left,
        y: elementRect.top
      };
      mouseStartRef.current = {
        x: clientX,
        y: clientY
      };
      touchStartRef.current = {
        x: clientX,
        y: clientY
      };

      return true;
    },
    [enabled]
  );

  /**
   * 处理鼠标按下事件
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      startDrag(e.clientX, e.clientY);
    },
    [startDrag]
  );

  /**
   * 处理触摸开始事件
   */
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      // 只处理单点触摸
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      e.preventDefault();
      startDrag(touch.clientX, touch.clientY);
    },
    [startDrag]
  );

  /**
   * 更新位置的通用处理函数
   */
  const updatePosition = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging || !elementRef.current) return;

      const deltaX = clientX - mouseStartRef.current.x;
      const deltaY = clientY - mouseStartRef.current.y;

      const newX = dragStartRef.current.x + deltaX;
      const newY = dragStartRef.current.y + deltaY;

      const constrained = constrainPosition(newX, newY);
      setPosition(constrained);
    },
    [isDragging, constrainPosition]
  );

  /**
   * 处理鼠标移动事件
   */
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      updatePosition(e.clientX, e.clientY);
    },
    [updatePosition]
  );

  /**
   * 处理触摸移动事件
   */
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      // 只处理单点触摸
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      e.preventDefault();
      updatePosition(touch.clientX, touch.clientY);
    },
    [updatePosition]
  );

  /**
   * 处理鼠标抬起事件
   */
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  /**
   * 处理触摸结束事件
   */
  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 监听全局鼠标事件和触摸事件
  useEffect(() => {
    if (isDragging) {
      // 鼠标事件
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // 触摸事件（移动端）
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchcancel', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // 窗口大小改变时重新约束位置
  useEffect(() => {
    const handleResize = () => {
      if (elementRef.current) {
        const elementRect = elementRef.current.getBoundingClientRect();
        const constrained = constrainPosition(elementRect.left, elementRect.top);
        setPosition(constrained);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [constrainPosition]);

  return {
    elementRef,
    position,
    isDragging,
    handleMouseDown,
    handleTouchStart,
    setPosition
  };
};
